import { useEffect, useRef } from 'react';

type Props = {
  hasMore: boolean;
  loading: boolean;
  error: boolean;
  paused: boolean;
  loadMore: () => void;
};

export function InfiniteProductLoader({ hasMore, loading, error, paused, loadMore }: Props) {
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (
      !hasMore ||
      loading ||
      error ||
      paused ||
      !sentinel.current ||
      !('IntersectionObserver' in window)
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { rootMargin: '500px 0px' },
    );
    observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [hasMore, loading, error, paused, loadMore]);

  if (!hasMore) return null;
  return (
    <div ref={sentinel} className="my-8 flex flex-col items-center gap-3">
      <p role="status" className="text-center text-sm text-gray-600">
        {error
          ? 'Could not load more products. Please try again.'
          : loading
            ? 'Loading more products…'
            : 'More products load as you scroll.'}
      </p>
      <button
        type="button"
        onClick={loadMore}
        disabled={loading || paused}
        className="min-h-11 rounded-lg border border-[#264B9A] px-6 py-2 font-semibold text-[#264B9A] hover:bg-blue-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#264B9A] disabled:opacity-50"
      >
        {error ? 'Try again' : loading ? 'Loading…' : 'Load more products'}
      </button>
    </div>
  );
}
